export default customElements.define('w-field-numberbox', class FieldNumberbox extends wealthify.componentBases.FieldText {

    constructor () {
        const config = {
            type: 'number',
            inputmode: 'numeric'
        };

        super(config);

        this.onConnectedCallback = () => {
            if(!this.props.allowedcharacters.isValueOverridden){
                this.allowedcharacters = this.allowed;
            }

            this.#restoreNoValueToZero();

            this.addEventListener('focus', () => {
                if(this.#shouldFormatEmpty) {
                    this.dom.fieldinput.value = '';
                }
            }, true);

            this.addEventListener('blur', this.#restoreNoValueToZero, true);

            this.addEventListener('wheel', event => {
                if(this.dom.fieldinput === document.activeElement) {
                    event.preventDefault();
                }
            });
        };
    }
    
    get readOnlyWidth () {
        return `calc(${this.value.toString().length}ch + .625rem)`;
    }

    static styleSelector = 'c-field-numberbox';
    static validationProps = [
        wealthify.FieldValidation.validationPropEnums.Min,
        wealthify.FieldValidation.validationPropEnums.Max,
        wealthify.FieldValidation.validationPropEnums.Step,
    ];

    static get observedProps () {
        return [
            new wealthify.Prop('decimalplaces', {
                default: 0,
                type: wealthify.propTypes.Number()
            }),
            new wealthify.Prop('is-non-incremental', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
        ];
    }

    static get formatEnums () {
        return {
            UkPhone: 'ukphone',
            UkMobile: 'ukmobile',
            SortCode: 'sortcode',
            AccountNumber: 'accountnumber'
        };
    }

    static get formatDictionary () {
        return {
            [this.formatEnums.UkPhone]: {
                'allowedcharacters': '[0-9+]',
                'type': 'tel',
                'inputmode': 'tel',
                'is-non-incremental': true,
                [wealthify.FieldValidation.messagePropEnums.Pattern]: 'This needs to be a valid UK telephone number (must start with a 0 or +44).',
                [wealthify.FieldValidation.validationPropEnums.Pattern]: /^0|\+44/,
                [wealthify.FieldValidation.validationPropEnums.MaxLength]: 13,
                variants: [
                    {
                        variantPattern: /^0/,
                        [wealthify.FieldValidation.validationPropEnums.MinLength]: 10,
                        [wealthify.FieldValidation.validationPropEnums.MaxLength]: 11,
                    },
                    {
                        variantPattern: /^\+44/,
                        [wealthify.FieldValidation.validationPropEnums.MinLength]: 12,
                        [wealthify.FieldValidation.validationPropEnums.MaxLength]: 13,
                    }
                ]
            },
            [this.formatEnums.UkMobile]: {
                'allowedcharacters': '[0-9+]',
                'type': 'tel',
                'inputmode': 'tel',
                'is-non-incremental': true,
                [wealthify.FieldValidation.messagePropEnums.Pattern]: 'This needs to be a valid UK mobile number (must start with a 07 or +447).',
                [wealthify.FieldValidation.validationPropEnums.Pattern]: /^07|\+447/,
                [wealthify.FieldValidation.validationPropEnums.MaxLength]: 13,
                variants: [
                    {
                        variantPattern: /^07/,
                        [wealthify.FieldValidation.validationPropEnums.MinLength]: 11,
                        [wealthify.FieldValidation.validationPropEnums.MaxLength]: 11,
                    },
                    {
                        variantPattern: /^\+447/,
                        [wealthify.FieldValidation.validationPropEnums.MinLength]: 13,
                        [wealthify.FieldValidation.validationPropEnums.MaxLength]: 13,
                    }
                ]
            },
            [this.formatEnums.SortCode]:
            {
                'placeholderprop': '00-00-00',
                'mask': {
                    delimiter: '-',
                    spacing: '2'
                },
                'is-non-incremental': true,
                [wealthify.FieldValidation.validationPropEnums.MinLength]: 8,
                [wealthify.FieldValidation.validationPropEnums.MaxLength]: 8,
                [wealthify.FieldValidation.messagePropEnums.MinLength]: 'This needs to contain 6 numbers.',
                [wealthify.FieldValidation.messagePropEnums.MaxLength]: 'This needs to contain 6 numbers.'
            },
            [this.formatEnums.AccountNumber]:
            {
                'is-non-incremental': true,
                [wealthify.FieldValidation.validationPropEnums.MinLength]: 8,
                [wealthify.FieldValidation.validationPropEnums.MaxLength]: 8,
                [wealthify.FieldValidation.validationPropEnums.Min]: 0,
            }
        };
    }

    get propCallbacks () {
        return {
            decimalplaces: () => {
                const hasDecimalPlaces = this.decimalplaces > 0;

                if (!hasDecimalPlaces) {
                    this[wealthify.FieldValidation.validationPropEnums.Step] = null;
                    this.dom.fieldinput.setAttribute('inputmode', 'numeric');

                    return;
                }

                this[wealthify.FieldValidation.validationPropEnums.Step] = '.'.padEnd(this.decimalplaces, 0) + '1';
                this.dom.fieldinput.setAttribute('inputmode', 'decimal');

                this.#restrictDecimalPlacesOverMax();

                // Capture both component value prop change, and direct input change
                this.addEventListener(this.events.ValueChange.name, this.#restrictsInvalidEntries.bind(this));
                this.dom.fieldinput.addEventListener('blur', this.#formatCurrentValueAsDecimal.bind(this));
            },
            isNonIncremental: () => {}
        };
    }

    get allowed () {
        const decimalPlacesPart = this.decimalplaces ? '.' : '';

        return this.constructor.formatDictionary[this.format]?.allowedcharacters ?? `[0-9${decimalPlacesPart}]`;
    }

    get #shouldFormatEmpty () {
        return !this.isNonIncremental && (this.dom.fieldinput.value === '' || parseInt(this.dom.fieldinput.value) === 0);
    }

    #restrictsInvalidEntries () {
        this.#restrictMultipleDecimalPoints();
        this.#restrictDecimalPlacesOverMax();
    }

    #restrictMultipleDecimalPoints () {
        const currentValue = this.dom.fieldinput.value.toString();
        const hasInvalidDecimalPlaces = currentValue.toString().split('.').length > 2;

        if (hasInvalidDecimalPlaces) {
            this.dom.fieldinput.value = this.value = Number.parseFloat(currentValue);
        }
    }

    #restrictDecimalPlacesOverMax () {
        const currentValue = this.dom.fieldinput.value.toString();
        const decimalPlaces = currentValue.toString().split('.')[1];

        if (decimalPlaces === undefined) {
            return;
        }

        const overflowAmount = decimalPlaces.length - this.decimalplaces;

        if (overflowAmount <= 0) {
            return;
        }

        this.dom.fieldinput.value = this.value = currentValue.slice(0, -overflowAmount);
    }

    #formatCurrentValueAsDecimal () {
        const currentValue = this.dom.fieldinput.value.toString();
        const hasNoValueOrSingleDecimalPoint = currentValue === '';

        if (hasNoValueOrSingleDecimalPoint) {
            // Singular decimal "." is not valid for type=number
            // Leading decimal ".0" or similar is valid so this is retained
            return this.dom.fieldinput.value = this.value = '';
        }

        const numberParts = currentValue.toString().split('.');
        const decimalPlacesLength = numberParts[1]?.length ?? 0;

        const decimalPlaceValue = !currentValue.includes('.') && this.decimalplaces > 0 ? '.' : '';
        const leadingZero = numberParts[0] === '' ? '0' : '';
        const underflowAmount = this.decimalplaces - decimalPlacesLength;

        this.dom.fieldinput.value = this.value = leadingZero + this.dom.fieldinput.value + decimalPlaceValue + ''.padEnd(underflowAmount, 0);
    }

    #restoreNoValueToZero () {
        if(this.#shouldFormatEmpty) {
            this.dom.fieldinput.value = '0';
            this.#formatCurrentValueAsDecimal();
        }
    }
});